import { useMemo } from "react";

import { ReportSectionsType } from "@api/graphql/types-and-hooks";
import { InitialAssessmentPageRoute } from "@pages/AssessmentReportV2Details/types";
import { EvaluationSubRoute } from "@pages/EvaluationDetails/EvaluationDetails";
import { ReportNode, useReport } from "@providers/ReportProvider";

export type FilterSectionSubRoute = Exclude<EvaluationSubRoute, "client-details" | "upload-files">;

export const getFilteredPageSectionsByLocation = (
  evaluation: ReportNode,
  pageLocation: FilterSectionSubRoute,
): ReportSectionsType[] => {
  const sectionEdges = evaluation?.reportSections?.edges || [];

  const locationOrder: InitialAssessmentPageRoute[] = ["report-part-1", "report-part-2", "review-and-sign"];

  let filteredSections: ReportSectionsType[];

  if (pageLocation === "review-and-sign") {
    filteredSections = sectionEdges
      .map((nodeObj) => nodeObj?.node)
      .filter((section) =>
        locationOrder.includes(section?.pageLocation as InitialAssessmentPageRoute),
      ) as ReportSectionsType[];

    // Sort by pageLocation based on locationOrder, then by rankOrder
    filteredSections?.sort((a, b) => {
      const locationComparison =
        locationOrder.indexOf(a.pageLocation as InitialAssessmentPageRoute) -
        locationOrder.indexOf(b.pageLocation as InitialAssessmentPageRoute);
      if (locationComparison === 0) {
        return a?.rankOrder - b?.rankOrder;
      }
      return locationComparison;
    });
  } else {
    filteredSections = sectionEdges
      .map((nodeObj) => nodeObj?.node)
      .filter((section) => section?.pageLocation === pageLocation) as ReportSectionsType[];
    filteredSections?.sort((a, b) => a?.rankOrder - b?.rankOrder);
  }

  return filteredSections ?? [];
};

export function useGetFilteredPageSectionsByLocation(pageLocation: FilterSectionSubRoute): ReportSectionsType[] {
  const { report } = useReport();
  // Stable reference until the report or location changes, so memoized consumers don't
  // recompute on every render.
  return useMemo(() => getFilteredPageSectionsByLocation(report, pageLocation), [report, pageLocation]);
}
